import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowLeft } from "lucide-react";
import { SITE } from "../data/content";
import useReveal from "../hooks/useReveal";

const SECTIONS = [
    {
        heading: "Information we collect",
        intro: "We may collect the following information:",
        bullets: [
            "Full name",
            "Email address",
            "Phone number",
            "City or location",
            "Information you voluntarily provide while registering for an event, booking a session, or contacting us",
            "Payment information (processed securely through third-party payment providers — we do not store your debit/credit card or banking details).",
        ],
    },
    {
        heading: "How we use your information",
        intro: "We use your information to:",
        bullets: [
            "Register you for workshops, retreats, therapy sessions, and wellness programs.",
            "Contact you regarding bookings, confirmations, reminders, and event updates.",
            "Respond to your enquiries and provide customer support.",
            "Improve our services and website experience.",
            "Share information about upcoming workshops, retreats, and offerings that may be relevant to you. You may unsubscribe from these communications at any time.",
        ],
    },
    {
        heading: "Information sharing",
        paragraphs: [
            "Your privacy is important to us. We do not sell, rent, or trade your personal information.",
            "We may share your information only:",
        ],
        bullets: [
            "With trusted service providers who assist us in operating our business (such as payment gateways or email service providers).",
            "When required by law or to comply with legal obligations.",
        ],
    },
    {
        heading: "Data security",
        paragraphs: [
            "We take appropriate technical and organisational measures to protect your personal information against unauthorised access, misuse, alteration, or disclosure. While no online system can guarantee absolute security, we are committed to protecting your information using industry-standard practices.",
        ],
    },
    {
        heading: "Cookies",
        paragraphs: [
            "Our website may use cookies or similar technologies to enhance your browsing experience and analyse website traffic. You can disable cookies through your browser settings if you prefer.",
        ],
    },
    {
        heading: "Your rights",
        intro: "You may request to:",
        bullets: [
            "Access the personal information we hold about you.",
            "Correct or update your information.",
            "Request deletion of your personal information.",
            "Opt out of marketing communications at any time.",
        ],
        paragraphs: [
            "To exercise any of these rights, please contact us using the details below.",
        ],
    },
    {
        heading: "Changes to this Privacy Policy",
        paragraphs: [
            "We may update this Privacy Policy from time to time to reflect changes in our services or legal requirements. Any updates will be posted on this page along with the revised effective date.",
            "By using the Soul Sync Healing website or submitting your information through our website, Meta lead forms, workshops, retreats, or other services, you agree to the terms outlined in this Privacy Policy.",
        ],
    },
];

export default function PrivacyPage() {
    useReveal();

    useEffect(() => {
        const prevTitle = document.title;
        const meta = document.querySelector('meta[name="description"]');
        const prevDesc = meta ? meta.getAttribute("content") : null;
        document.title = "Privacy Policy · Soul Sync Healing";
        if (meta) {
            meta.setAttribute(
                "content",
                "How Soul Sync Healing collects, uses and protects your personal information when you book sessions, register for workshops, or interact with us online.",
            );
        }
        return () => {
            document.title = prevTitle;
            if (meta && prevDesc !== null) {
                meta.setAttribute("content", prevDesc);
            }
        };
    }, []);

    return (
        <div className="pt-24" data-testid="privacy-page">
            <article className="max-w-3xl mx-auto px-6 lg:px-0 py-16 lg:py-24">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                    data-testid="privacy-back"
                >
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Back home
                </Link>

                <div className="mt-10 ssh-reveal">
                    <p className="ssh-eyebrow">Legal</p>
                    <h1 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#2C2B29]">
                        Privacy Policy
                    </h1>
                    <p className="mt-5 text-xs uppercase tracking-[0.18em] text-[#75736F]">
                        Effective Date · 28 June 2026
                    </p>
                </div>

                <div className="mt-12 space-y-6 ssh-reveal">
                    <p className="text-lg leading-[1.85] text-[#2C2B29] font-light">
                        Welcome to <strong>Soul Sync Healing</strong>. We
                        respect your privacy and are committed to protecting
                        the personal information you share with us.
                    </p>
                    <p className="text-lg leading-[1.85] text-[#2C2B29] font-light">
                        This Privacy Policy explains how we collect, use, and
                        safeguard your information when you visit our website,
                        register for our workshops, retreats, therapy sessions,
                        wellness programs, or interact with us through social
                        media, online forms, or other communication channels.
                    </p>
                </div>

                {SECTIONS.map((s, idx) => (
                    <section key={idx} className="mt-14 ssh-reveal">
                        <h2 className="font-serif text-2xl sm:text-3xl text-[#2C2B29] leading-tight">
                            {s.heading}
                        </h2>
                        <span
                            className="mt-4 inline-block w-10 h-px bg-[#A68A6B]"
                            aria-hidden="true"
                        />
                        <div className="mt-6 space-y-5">
                            {s.intro && (
                                <p className="text-lg leading-[1.85] text-[#2C2B29] font-light">
                                    {s.intro}
                                </p>
                            )}
                            {s.bullets && (
                                <ul className="space-y-3 pl-0">
                                    {s.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="flex gap-3 text-base lg:text-lg leading-[1.75] text-[#2C2B29] font-light"
                                        >
                                            <span className="text-[#A68A6B] mt-2 inline-block w-3 h-px bg-[#A68A6B]" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {s.paragraphs &&
                                s.paragraphs.map((p, i) => (
                                    <p
                                        key={i}
                                        className="text-lg leading-[1.85] text-[#2C2B29] font-light"
                                    >
                                        {p}
                                    </p>
                                ))}
                        </div>
                    </section>
                ))}

                {/* Contact block */}
                <section
                    className="mt-16 ssh-reveal bg-[#EFEBE4] p-8 lg:p-10"
                    data-testid="privacy-contact"
                >
                    <p className="ssh-eyebrow">Contact us</p>
                    <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-[#2C2B29]">
                        Soul Sync Healing
                    </h2>
                    <ul className="mt-6 space-y-4 text-base text-[#2C2B29]">
                        <li>
                            <a
                                href={`mailto:${SITE.email}`}
                                className="inline-flex items-center gap-3 hover:text-[#A68A6B] transition-colors"
                                data-testid="privacy-email"
                            >
                                <Mail
                                    className="h-4 w-4 text-[#A68A6B]"
                                    strokeWidth={1.5}
                                />
                                {SITE.email}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`https://wa.me/${SITE.whatsappRaw}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-3 hover:text-[#A68A6B] transition-colors"
                                data-testid="privacy-phone"
                            >
                                <Phone
                                    className="h-4 w-4 text-[#A68A6B]"
                                    strokeWidth={1.5}
                                />
                                {SITE.whatsappDisplay}
                            </a>
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    );
}
